import type { ApiClient } from '@carbonwebmail/api-client'
import type { ContactDetailDto } from '@carbonwebmail/contracts'
import { useCallback } from 'react'
import { Link, useParams } from 'react-router'
import { Async } from '../../ui/Async.js'
import { when } from '../../ui/format.js'
import { StatusPill } from '../../ui/StatusPill.js'
import { Table, Td, Tr } from '../../ui/Table.js'
import { useLoaded } from '../../ui/useLoaded.js'
import { CONSENT, REASONS, SEND_STATUS } from '../deliverability/statusLabels.js'

/**
 * Everything known about one contact.
 *
 * The banner at the top is the reason this screen exists. A contact can read
 * `subscribed` while a hard bounce sits against their address, and nothing will
 * ever reach them — a list that shows only the status makes somebody wonder why
 * their campaign reached fewer people than it said. Here the two facts sit
 * together, with the reason and the date.
 *
 * The consent trail is oldest first, because it is a story: asked, confirmed,
 * left. Everything else on the screen is newest first, because it is a log.
 *
 * A page rather than a drawer over the list. The design offers both; two
 * renderings of one thing is two places for the suppression banner to be
 * forgotten, and the banner is the point.
 */
export function ContactDetailPage({ client }: { readonly client: ApiClient }) {
  const { contactId = '' } = useParams()
  const load = useCallback(() => client.contact(contactId), [client, contactId])
  const { state } = useLoaded<ContactDetailDto>(
    load,
    'We could not load that contact. Please try again in a moment.',
  )

  return (
    <Async
      state={state}
      skeleton={<p className="text-[13px] text-t3">Loading…</p>}
      onRetry={() => window.location.reload()}
    >
      {(contact) => <Detail contact={contact} />}
    </Async>
  )
}

const STATUS_TONE = {
  subscribed: 'ok',
  unsubscribed: 'neutral',
  cleaned: 'neutral',
} as const

function Detail({ contact }: { readonly contact: ContactDetailDto }) {
  return (
    <>
      <header className="mb-6">
        <Link className="font-medium text-[13px] text-t3 hover:text-t1" to="/contacts">
          ← Contacts
        </Link>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <h1 className="font-bold text-[22px] text-t1 -tracking-[0.01em]">
            {contact.name === '' ? contact.email : contact.name}
          </h1>
          <StatusPill tone={STATUS_TONE[contact.status]}>{contact.status}</StatusPill>
        </div>
        <p className="mt-1 text-[13px] text-t2">
          {contact.email}
          {contact.company === null ? null : ` · ${contact.company}`} · added{' '}
          {when(contact.createdAt)}
        </p>
      </header>

      {/*
        The whole reason for this screen. "Subscribed" and "will actually
        receive mail" are different questions, and only this answers the second.
      */}
      {contact.suppressedFor === null ? null : (
        <div
          role="status"
          className="mb-6 rounded-card border border-danger bg-danger-soft px-4 py-3 text-[13px] text-danger"
        >
          <strong className="font-semibold">
            Nothing will reach this address — {REASONS[contact.suppressedFor].label}
          </strong>
          {contact.suppressedAt === null ? null : (
            <span className="text-t2"> · since {when(contact.suppressedAt)}</span>
          )}
          <span className="block text-t2">
            Every campaign skips it, and the check is made again at the moment each message is sent.
          </span>
        </div>
      )}

      {contact.custom.length === 0 ? null : (
        <section className="mb-6">
          <h2 className="mb-3 font-bold text-[15px] text-t1">Fields</h2>
          <Table head={['Field', 'Value', 'Merge token']} minWidth={640}>
            {contact.custom.map((field) => (
              <Tr key={field.key}>
                <Td strong>{field.label}</Td>
                {/* An em dash rather than a blank: "we never collected this" is
                    a fact, and an empty cell looks like a rendering fault. */}
                <Td>{field.value === '' ? '—' : field.value}</Td>
                <Td mono>{`{{${field.key}}}`}</Td>
              </Tr>
            ))}
          </Table>
        </section>
      )}

      <section className="mb-6">
        <h2 className="font-bold text-[15px] text-t1">Consent</h2>
        <p className="mt-1 mb-3 text-[13px] text-t2">
          Oldest first. Append-only — nothing here can be edited or removed, because it is what gets
          produced in a dispute.
        </p>
        {contact.consent.length === 0 ? (
          <p className="rounded-card border border-line bg-surface px-4 py-6 text-center text-[13px] text-t3">
            Nothing recorded for this address.
          </p>
        ) : (
          <Table head={['When', 'What happened', 'Where from']} minWidth={640}>
            {contact.consent.map((event) => (
              <Tr key={`${event.type}-${event.occurredAt}`}>
                <Td>{when(event.occurredAt)}</Td>
                <Td>
                  <StatusPill tone={toneOfConsent(event.type)}>
                    {labelOfConsent(event.type)}
                  </StatusPill>
                </Td>
                <Td>{event.source}</Td>
              </Tr>
            ))}
          </Table>
        )}
      </section>

      <section>
        <h2 className="mb-3 font-bold text-[15px] text-t1">Mail</h2>
        {contact.sends.length === 0 ? (
          <p className="rounded-card border border-line bg-surface px-4 py-6 text-center text-[13px] text-t3">
            Nothing has been sent to this address yet.
          </p>
        ) : (
          <Table head={['When', 'Campaign', 'Outcome', 'Reason']} minWidth={720}>
            {contact.sends.map((send) => (
              <Tr key={`${send.createdAt}-${send.status}`}>
                <Td>{when(send.createdAt)}</Td>
                <Td>{send.campaignName ?? '—'}</Td>
                <Td>
                  <StatusPill tone={toneOfSend(send.status)}>{send.status}</StatusPill>
                </Td>
                <Td>{send.reason ?? '—'}</Td>
              </Tr>
            ))}
          </Table>
        )}
      </section>
    </>
  )
}

/**
 * The two lookups come from the wire as plain strings rather than unions.
 *
 * The contract narrows the *listing* types because those screens are keyed by
 * them; here the rows are a history and a value the enum grows into should show
 * as itself rather than crash the page. So an unrecognised one falls back
 * instead of failing — the opposite trade from `statusLabels`, and deliberate.
 */
function toneOfConsent(type: string) {
  return CONSENT[type as keyof typeof CONSENT]?.tone ?? 'neutral'
}

function labelOfConsent(type: string) {
  return CONSENT[type as keyof typeof CONSENT]?.label ?? type.replace(/_/g, ' ')
}

function toneOfSend(status: string) {
  return SEND_STATUS[status as keyof typeof SEND_STATUS]?.tone ?? 'neutral'
}
